<template>
  <article class="book-card h-100">
    <BookCover
      :color="coverColor"
      :title="coverTitle"
      :logo="logo"
      :publisher="publisher"
    />

    <h2 class="book-card__title">{{ title }}</h2>

    <div class="book-card__pills" role="list" aria-label="جزئیات کتاب">
      <span class="book-card__pill book-card__pill--level" role="listitem">{{ level }}</span>
      <span class="book-card__pill" role="listitem">{{ units }}</span>
    </div>

    <p class="book-card__desc">{{ description }}</p>

    <div class="book-card__progress-row">
      <span :id="progressLabelId">{{ progressLabel }}</span>
    </div>

    <div
      class="book-card__progress-track"
      role="progressbar"
      :aria-valuenow="progress"
      aria-valuemin="0"
      aria-valuemax="100"
      :aria-labelledby="progressLabelId"
    >
      <div
        class="book-card__progress-fill"
        :style="{ width: `${progress}%` }"
      />
    </div>

    <button
      type="button"
      class="book-card__continue-btn"
      :aria-label="`ادامه ${title}`"
      @click="$emit('continue')"
    >
      ادامه ←
    </button>
  </article>
</template>

<script setup lang="ts">
import { computed } from "vue";
import BookCover from "@/components/BookCover.vue";

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  description: {
    type: String,
    required: true,
  },
  level: {
    type: String,
    required: true,
  },
  units: {
    type: String,
    required: true,
  },
  progress: {
    type: Number,
    required: true,
    validator: (value) => value >= 0 && value <= 100,
  },
  coverColor: {
    type: String,
    required: true,
    validator: (value) => ["blue", "purple"].includes(value),
  },
  coverTitle: {
    type: String,
    required: true,
  },
  logo: {
    type: String,
    required: true,
  },
  publisher: {
    type: String,
    required: true,
  },
});

defineEmits(["continue"]);

const progressLabel = computed(() => `${props.progress}% پیشرفت`);

const progressLabelId = computed(() =>
  `book-progress-${props.title.replace(/\s+/g, "-").toLowerCase()}`
);
</script>

<style scoped>
.book-card {
  background: var(--vocab-card-bg, #FFFFFF);
  border: 1.5px solid var(--vocab-card-border, #ECE4D9);
  border-radius: 20px;
  padding: 28px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 2px 12px rgba(76, 55, 42, 0.05);
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    border-color 0.2s ease;
}

.book-card:hover {
  transform: translateY(-3px);
  border-color: rgba(109, 94, 248, 0.35);
  box-shadow: 0 10px 28px rgba(109, 94, 248, 0.1);
}

.book-card:hover :deep(.book-cover__book) {
  transform: rotateY(-7deg) rotateX(1deg) translateY(-4px);
}

.book-card:hover :deep(.book-cover__shadow) {
  opacity: 0.85;
  transform: translateX(-50%) scale(1.06);
}

.book-card__title {
  font-size: 18px;
  font-weight: 700;
  margin: 0 0 14px;
  color: var(--vocab-text-primary, #4C372A);
}

.book-card__pills {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
}

.book-card__pill {
  background: var(--vocab-pill-bg, #FBF7FC);
  color: var(--vocab-text-secondary, #8C8176);
  border: 1px solid var(--vocab-card-border, #ECE4D9);
  font-size: 13px;
  font-weight: 600;
  padding: 7px 14px;
  border-radius: 20px;
}

.book-card__pill--level {
  color: var(--vocab-accent-purple, #6D5EF8);
  background: rgba(109, 94, 248, 0.08);
  border-color: rgba(109, 94, 248, 0.18);
}

.book-card__desc {
  color: var(--vocab-text-secondary, #8C8176);
  font-size: 14px;
  line-height: 1.6;
  margin: 0 0 24px;
  flex-grow: 1;
}

.book-card__progress-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  color: var(--vocab-text-secondary, #8C8176);
  margin-bottom: 11px;
}

.book-card__progress-track {
  width: 100%;
  height: 8px;
  background: var(--vocab-progress-track, #F0EBE3);
  border-radius: 6px;
  overflow: hidden;
  margin-bottom: 20px;
}

.book-card__progress-fill {
  height: 100%;
  background: var(--vocab-progress-fill, #6D5EF8);
  border-radius: 4px;
  transition: width 0.6s cubic-bezier(0.22, 1, 0.36, 1);
}

.book-card__continue-btn {
  border: 1.5px solid var(--vocab-accent-gold, #6D5EF8);
  color: var(--vocab-accent-gold, #6D5EF8);
  background: transparent;
  padding: 12px 22px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  align-self: flex-start;
  font-family: inherit;
  transition:
    background 0.2s ease,
    color 0.2s ease,
    transform 0.2s ease,
    box-shadow 0.2s ease;
}

.book-card__continue-btn:hover {
  background: rgba(109, 94, 248, 0.08);
  transform: translateX(-2px);
  box-shadow: 0 4px 14px rgba(109, 94, 248, 0.15);
}

.book-card__continue-btn:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(109, 94, 248, 0.25);
}
</style>
